import React, { useEffect, useState } from "react";
import wasmInit, { init, stage, elab } from "staged-comp";
import CodeMirror from "@uiw/react-codemirror";
import { HighlightedOutput } from "./HighlightedOutput";
import { twoLttLanguage, twoLttTheme } from "./twoLttLanguage";
import styles from "./index.module.scss";
export var App = function () {
    var _a = useState("const nat1_to_nat0\n  : (_: Nat1) -> Nat0\n  = nat_elim1 (^Nat0) zero0 ((m: ^Nat0) => <succ0 m>)\nin\n\nconst add1\n  : (_: Nat1) -> (_: Nat1) -> Nat1\n  = (a: Nat1) => nat_elim1 Nat1 a succ1\nin\n\nconst a: Nat1 = succ1 (succ1 zero1) in\nconst b: Nat1 = succ1 zero1 in\n\nlet c: Nat0 = ~(nat1_to_nat0 (add1 a b)) in\nc\n"), input = _a[0], setInput = _a[1];
    var _b = useState(false), err = _b[0], setErr = _b[1];
    var PLACEHOLDER = "Output goes here...";
    var _c = useState(PLACEHOLDER), output = _c[0], setOutput = _c[1];
    var onChange = React.useCallback(function (value, _) {
        setInput(value);
    }, []);
    useEffect(function () {
        wasmInit().then(init);
    }, []);
    return React.createElement("div", { className: styles.page },
        React.createElement("header", { className: styles.hero },
            React.createElement("span", { className: styles.badge }, "Two-Level Type Theory"),
            React.createElement("h1", { className: styles.title }, "2LTT Playground"),
            React.createElement("p", { className: styles.subtitle }, "2LTT (Two-Level Type Theory) is a type system with two universes: stage 0 for runtime values and stage 1 for compile-time computation. This playground lets you experiment with the formal system, elaborate terms or stage them to see the two-level structure."),
            React.createElement("p", { className: styles.author }, "Paper by Andr\u00E1s Kov\u00E1cs, thanks for the awesome work."),
            React.createElement("a", { href: "https://andraskovacs.github.io/pdfs/2ltt.pdf", target: "_blank", rel: "noopener noreferrer", className: styles.paperButton },
                React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                    React.createElement("path", { d: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" }),
                    React.createElement("polyline", { points: "15 3 21 3 21 9" }),
                    React.createElement("line", { x1: "10", y1: "14", x2: "21", y2: "3" })),
                "Read the Paper")),
        React.createElement("section", { className: styles.explanations },
            React.createElement("div", { className: styles.explainCard + " " + styles.stage0 },
                React.createElement("h3", { className: styles.explainTitle },
                    React.createElement("span", { className: styles.type0, "data-tooltip": "Runtime" }, "Stage 0"),
                    " \u00B7 Runtime"),
                React.createElement("div", { className: styles.explainText },
                    React.createElement("p", null,
                        React.createElement("span", { className: styles.type0 }, "Nat0"),
                        ", ",
                        React.createElement("span", { className: styles.type0 }, "zero0"),
                        ", ",
                        React.createElement("span", { className: styles.type0 }, "succ0"),
                        ", ",
                        React.createElement("span", { className: styles.type0 }, "nat_elim0")),
                    React.createElement("p", null, "Values that exist when your program runs."))),
            React.createElement("div", { className: styles.explainCard + " " + styles.stage1 },
                React.createElement("h3", { className: styles.explainTitle },
                    React.createElement("span", { className: styles.type1, "data-tooltip": "Compile-time" }, "Stage 1"),
                    " \u00B7 Compile-time"),
                React.createElement("div", { className: styles.explainText },
                    React.createElement("p", null,
                        React.createElement("span", { className: styles.type1 }, "Nat1"),
                        ", ",
                        React.createElement("span", { className: styles.type1 }, "zero1"),
                        ", ",
                        React.createElement("span", { className: styles.type1 }, "succ1"),
                        ", ",
                        React.createElement("span", { className: styles.type1 }, "nat_elim1")),
                    React.createElement("p", null, "Values that exist when your program is being built."))),
            React.createElement("div", { className: styles.explainCard + " " + styles.operators },
                React.createElement("h3", { className: styles.explainTitle }, "Special operators"),
                React.createElement("div", { className: styles.explainText },
                    React.createElement("p", null,
                        React.createElement("code", { className: styles.op }, "^"),
                        " use a ",
                        React.createElement("span", { className: styles.type0, "data-tooltip": "Runtime" }, "stage 0"),
                        " value at ",
                        React.createElement("span", { className: styles.type1, "data-tooltip": "Compile-time" }, "stage 1")),
                    React.createElement("p", null,
                        React.createElement("code", { className: styles.op }, "~"),
                        " compiles ",
                        React.createElement("span", { className: styles.type1, "data-tooltip": "Compile-time" }, "stage 1"),
                        " to ",
                        React.createElement("span", { className: styles.type0, "data-tooltip": "Runtime" }, "stage 0")),
                    React.createElement("p", null,
                        React.createElement("code", { className: styles.op }, "< >"),
                        " keep term in ",
                        React.createElement("span", { className: styles.type1, "data-tooltip": "Compile-time" }, "stage 1"))))),
        React.createElement("main", { className: styles.main },
            React.createElement("div", { className: styles.card },
                React.createElement("div", { className: styles.cardHeader },
                    React.createElement("h2", { className: styles.cardTitle }, "Source"),
                    React.createElement("span", { className: styles.headerSpacer, "aria-hidden": true })),
                React.createElement("div", { className: styles.editorWrap },
                    React.createElement(CodeMirror, { value: input, onChange: onChange, theme: twoLttTheme, height: "100%", minHeight: "200px", extensions: [twoLttLanguage.extension] }))),
            React.createElement("div", { className: styles.card },
                React.createElement("div", { className: styles.cardHeader },
                    React.createElement("h2", { className: styles.cardTitle }, "Output"),
                    React.createElement("div", { className: styles.buttons },
                        React.createElement("button", { className: styles.btn, onClick: function () {
                                try {
                                    var e = elab(input);
                                    setOutput(e);
                                    setErr(false);
                                }
                                catch (err) {
                                    setOutput("" + err);
                                    setErr(true);
                                }
                            } }, "Elaborate"),
                        React.createElement("button", { className: styles.btn, onClick: function () {
                                try {
                                    var e = stage(input);
                                    setOutput(e);
                                    setErr(false);
                                }
                                catch (err) {
                                    setOutput("" + err);
                                    setErr(true);
                                }
                            } }, "Stage"))),
                output === PLACEHOLDER ? (React.createElement("div", { className: styles.outputEmpty },
                    React.createElement("div", { className: styles.outputEmptyIcon },
                        React.createElement("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" },
                            React.createElement("polyline", { points: "16 18 22 12 16 6" }),
                            React.createElement("polyline", { points: "8 6 2 12 8 18" }))),
                    React.createElement("p", { className: styles.outputEmptyText }, "Click a button above to see the result"),
                    React.createElement("div", { className: styles.outputEmptyHints },
                        React.createElement("p", null,
                            React.createElement("strong", null, "Elaborate"),
                            ": Resolves and validates the term, shows the elaborated form with stage annotations normalized to stage 0."),
                        React.createElement("p", null,
                            React.createElement("strong", null, "Stage"),
                            ": Evaluates runtime parts and shows what remains as compile-time code (in quotes) vs what becomes runtime values.")))) : (React.createElement(HighlightedOutput, { "data-err": err.toString(), className: styles.output }, output)))),
        React.createElement("footer", { className: styles.footer }, "Implementation by Quentin Januel and Zakaria Djebbes."));
};
